import { Link as LinkIcon, X } from "lucide-react";

const VIDEO_EXTS = [".mp4", ".webm", ".mov", ".ogv", ".ogg", ".m4v"];
function isVideoUrl(url) {
  if (!url) return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return VIDEO_EXTS.some((ext) => clean.endsWith(ext));
}

/**
 * Normalize common image-host page URLs into direct-image URLs so
 * users can paste whichever they grabbed without breaking the preview.
 *
 *   https://imgur.com/abc       → https://i.imgur.com/abc.jpeg
 *   https://imgur.com/a/abc     → https://i.imgur.com/abc.jpeg  (album → first img guess)
 *   https://postimg.cc/abc      → leave alone (postimg page; user must use "Direct link")
 *   https://ibb.co/abc          → leave alone (ibb page; user must use "Direct links" → "HTML")
 *
 * Imgur is the only one we can safely rewrite because their CDN ignores
 * the extension you request (i.imgur.com/abc.jpeg works even if the
 * file is actually PNG).
 */
function normalizeImageUrl(raw) {
  if (!raw) return raw;
  const url = raw.trim();
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    if (host === "imgur.com") {
      // Strip /a/, /gallery/, etc. — grab the last path segment
      const parts = u.pathname.split("/").filter(Boolean);
      const id = parts[parts.length - 1];
      if (id && !/\.(png|jpg|jpeg|gif|webp|mp4)$/i.test(id)) {
        return `https://i.imgur.com/${id}.jpeg`;
      }
    }
  } catch (_) {
    // not a valid URL — return as-is
  }
  return url;
}

/**
 * URL-only media input — paste a direct link to an image or video and it
 * renders a preview. NOTHING is uploaded or stored on your server.
 */
export default function ImageUploadInput({
  value = "",
  onChange,
  placeholder = "Paste image or video URL (e.g. https://i.imgur.com/abc.jpeg)",
  testid,
  compact = false,
}) {
  const commit = (raw) => {
    const fixed = normalizeImageUrl(raw);
    onChange?.(fixed);
  };

  if (compact) {
    return (
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-xs text-white font-mono"
      />
    );
  }

  const hasValue = !!value;
  const video = isVideoUrl(value);

  return (
    <div
      className="border border-[var(--line-strong)] hover:border-[var(--accent)]/60 transition bg-black/40 px-4 py-4 flex items-center gap-4"
      data-testid={testid ? `${testid}-dropzone` : "image-input-zone"}
    >
      {hasValue ? (
        video ? (
          <video
            src={value}
            muted
            playsInline
            className="w-16 h-16 object-cover border border-[var(--line)] flex-shrink-0 bg-black"
            onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
          />
        ) : (
          <img
            src={value}
            alt=""
            className="w-16 h-16 object-cover border border-[var(--line)] flex-shrink-0"
            onError={(e) => { e.currentTarget.style.opacity = "0.2"; }}
          />
        )
      ) : (
        <div className="w-16 h-16 border border-[var(--line)] flex items-center justify-center text-[var(--muted)] flex-shrink-0">
          <LinkIcon size={20} />
        </div>
      )}

      <div className="flex-1 min-w-0 space-y-1.5">
        <input
          data-testid={testid}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-xs text-white font-mono"
        />
        <div className="font-mono text-[10px] text-[var(--muted)] leading-relaxed">
          // Paste a <span className="text-white">DIRECT</span> image URL — ends in <span className="text-[var(--accent)]">.png / .jpg / .gif / .mp4</span>.
          {" "}On imgur, right-click the image and choose <span className="text-white">{`"Copy image address"`}</span>.
          {" "}Imgur page URLs are auto-fixed on blur.
        </div>
      </div>

      {hasValue && (
        <button
          type="button"
          onClick={() => onChange?.("")}
          className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-1.5 flex-shrink-0"
          aria-label="Clear URL"
          data-testid={testid ? `${testid}-clear` : undefined}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
}
